import { useState } from "react";
import { Save, X } from "lucide-react";
import { money } from "@/components/billing/RateTable";
import { api, formatApiError } from "@/lib/api";

function CardRow({ provider, rateKey, label, hint, card, fallback, testKey, onChanged, onError }) {
  const [fee, setFee] = useState(card?.fee ?? "");
  const [busy, setBusy] = useState(false);
  const dirty = String(fee) !== String(card?.fee ?? "");
  async function save() {
    setBusy(true);
    try {
      const response = await api.put(`/evaluation-billing/provider-rates/${provider.id}`, { rate_key: rateKey, fee: Number(fee) });
      onChanged(`${provider.name} · ${label} rate saved.${response.data.applied_to ? ` Applied to ${response.data.applied_to} unpaid evaluation(s).` : ""}`);
    } catch (requestError) {
      onError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }
  async function clear() {
    setBusy(true);
    try {
      await api.delete(`/evaluation-billing/provider-rates/${provider.id}`, { params: { rate_key: rateKey } });
      onChanged(`${provider.name} · ${label} rate removed.`);
    } catch (requestError) {
      onError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }
  return (
    <tr data-testid={`provider-rate-row-${provider.id}-${testKey}`}>
      <td><strong>{label}</strong>{hint && <small className="fee-source">{hint}</small>}</td>
      <td><input type="number" min="0" step="0.01" placeholder={fallback} value={fee} data-testid={`provider-rate-fee-${provider.id}-${testKey}`} onChange={(e) => setFee(e.target.value)} /></td>
      <td><small data-testid={`provider-rate-current-${provider.id}-${testKey}`}>{card ? `${money(card.fee)} · ${card.updated_by_name} · ${new Date(card.updated_at).toLocaleDateString()}` : `Not set — ${fallback}`}</small></td>
      <td className="credential-actions rate-actions">
        <button type="button" className="button button-secondary" disabled={busy || !dirty || fee === ""} data-testid={`provider-rate-save-${provider.id}-${testKey}`} onClick={save}><Save size={13} /> Save</button>
        {card && <button type="button" className="projection-icon-button" aria-label={`Clear ${label} rate`} disabled={busy} data-testid={`provider-rate-clear-${provider.id}-${testKey}`} onClick={clear}><X size={14} /></button>}
      </td>
    </tr>
  );
}

const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const ProviderRateCards = ({ data, onChanged, onError }) => {
  const withCards = new Set(data.provider_cards.map((c) => c.provider_id));
  const [providerId, setProviderId] = useState(data.providers.find((p) => withCards.has(p.id))?.id || data.providers[0]?.id || "");
  const provider = data.providers.find((p) => p.id === providerId);
  const cards = data.provider_cards.filter((c) => c.provider_id === providerId);
  const categoryCard = (type) => cards.find((c) => c.rate_key === `category:${type}`);
  const typeCard = (entry) => cards.find((c) => c.rate_key === entry.rate_key);
  const fallbackFor = (entry) => {
    const categories = entry.evaluation_types.map((type) => categoryCard(type)).filter(Boolean);
    if (categories.length === 1) return `falls back to ${money(categories[0].fee)} (${categories[0].evaluation_type} category rate)`;
    if (categories.length > 1) return `falls back to the category rate (${categories.map((c) => `${c.evaluation_type} ${money(c.fee)}`).join(", ")})`;
    const defaults = [...new Set(entry.default_provider_fees)];
    return defaults.length === 1 ? `falls back to the client default ${money(defaults[0])} (70%)` : `falls back to the client default (70% of the client fee: ${entry.default_provider_fees.map(money).join(" / ")})`;
  };
  return (
    <section className="compliance-card" data-testid="provider-rate-cards">
      <div className="section-heading">
        <div>
          <h3>Provider rate cards</h3>
          <p>What each provider is paid, separate from client fees. A rate for a <strong>specific evaluation type</strong> (Psych initial, AAC eval…) wins; otherwise the <strong>category</strong> rate (OT, Psychological…) applies; with neither, the provider is paid the client's default — 70% of the client fee — editable on the record.</p>
        </div>
        <select value={providerId} data-testid="provider-rate-select" onChange={(e) => setProviderId(e.target.value)}>
          {data.providers.map((p) => <option key={p.id} value={p.id}>{p.name}{withCards.has(p.id) ? " ●" : ""} · {p.discipline}</option>)}
        </select>
      </div>
      {provider ? (
        <>
          <h4 className="rate-card-subheading">Category rates <small>(fallback for every evaluation type in the category)</small></h4>
          <div className="credential-table-wrap">
            <table>
              <thead><tr><th>Category</th><th>Provider fee</th><th>Current card</th><th /></tr></thead>
              <tbody>
                {data.evaluation_types.map((type) => <CardRow key={`${providerId}-cat-${type}-${categoryCard(type)?.updated_at || "none"}`} provider={provider} rateKey={`category:${type}`} label={type} card={categoryCard(type)} fallback="70% of the client fee" testKey={slug(type)} onChanged={onChanged} onError={onError} />)}
              </tbody>
            </table>
          </div>
          <h4 className="rate-card-subheading">Specific evaluation types <small>(from the clients' rate tables — override the category rate)</small></h4>
          <div className="credential-table-wrap">
            <table>
              <thead><tr><th>Evaluation type</th><th>Provider fee</th><th>Current card</th><th /></tr></thead>
              <tbody>
                {data.rate_type_names.map((entry) => <CardRow key={`${providerId}-type-${entry.rate_key}-${typeCard(entry)?.updated_at || "none"}`} provider={provider} rateKey={entry.rate_key} label={entry.name} hint={`${entry.evaluation_types.join(", ")} · ${[...new Set(entry.clients)].join(", ")}`} card={typeCard(entry)} fallback={fallbackFor(entry)} testKey={`type-${slug(entry.name)}`} onChanged={onChanged} onError={onError} />)}
              </tbody>
            </table>
            {!data.rate_type_names.length && <p className="operations-empty-state">No evaluation types in the rate tables yet.</p>}
          </div>
        </>
      ) : <p className="operations-empty-state">No approved providers.</p>}
    </section>
  );
};
